import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TranslocoService } from '@jsverse/transloco';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ContactUsForm } from 'src/app/models/contact-us.model';
import { ContactUsValidation } from 'src/app/models/enums/conatct-us-validation.enum';
import { EmailType } from 'src/app/models/enums/email-type.enum';
import { ManagementApiService } from 'src/app/services';
import { EmailSenderService } from 'src/app/services/email-sender.service';
import { EncrytionDecryptionHelperService } from 'src/app/services/encrytion-decryption-helper.service';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { environment } from 'src/environments/environment';
import { CustomToastService } from './../../../../services/custom-toast.service';
import { EnvironmentService } from 'src/app/services/environment.service';
declare global {
    interface Window {
        grecaptcha: any;
        recaptchaReady: boolean;
    }
}

@Component({
    selector: 'app-contact-us-details',
    templateUrl: './contact-us-details.component.html',
    styleUrls: ['./contact-us-details.component.scss'],
    standalone: false,
})
export class ContactUsDetailsComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    contactUsForm: UntypedFormGroup;
    contactUsDetails: ContactUsForm;
    contactUsValidation = ContactUsValidation;
    countryList: string[];
    productList: string[];
    topicList: string[];
    serviceList: string[];
    industryList: string[];
    productRelatedTopics: string[];
    isProductDropDownList: boolean;
    isServiceDropDownList: boolean;
    isMailSent: boolean;
    @ViewChild('captchaRef', { static: false }) captchaRef!: ElementRef;
    constructor(
        private formBuilder: UntypedFormBuilder,
        private managementApiService: ManagementApiService,
        private customToastService: CustomToastService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private emailSenderService: EmailSenderService,
        private globalEmittersService: GlobalEmittersService,
        private transLocoService: TranslocoService,
        private encrytionDecryptionHelperService: EncrytionDecryptionHelperService,
        private EnvironmentService: EnvironmentService
    ) {
        this.countryList = [];
        this.productList = [];
        this.topicList = [];
        this.serviceList = [];
        this.industryList = [];
        this.productRelatedTopics = [];
        this.isProductDropDownList = false;
        this.isServiceDropDownList = false;
        this.isMailSent = false;
    }

    ngOnInit(): void {
        this.createFormControls();

        this.globalEmittersService.isLanguageSetUp.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            if (data) {
                this.getDropDownsData();
            }
        });

        this.globalEmittersService.isUserDefaultsLoaded.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            if (data) {
                this.getDropDownsData();
            }
        });
        this.globalEmittersService.contactLinkClick.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.isMailSent = false;
            }
        });
    }

    handleExpire() {
        this.contactUsForm.get('captcha').reset();
    }

    // * method to create the form controls
    createFormControls() {
        this.contactUsForm = this.formBuilder.group({
            inquireyMessage: [null, [Validators.required, Validators.maxLength(500)]],
            topic: [null, Validators.required],
            productType: [null],
            serviceType: [null],
            firstName: [null, Validators.required],
            lastName: [null, Validators.required],
            email: [null, [Validators.required, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]],
            phone: [null, Validators.required],
            company: [null, Validators.required],
            address: [null, Validators.required],
            city: [null, Validators.required],
            zipCode: [null, Validators.required],
            country: [null, Validators.required],
            industry: [null, Validators.required],
            subscriptionCheck: [null, Validators.required],
            captchaCode: [null, Validators.required],
        });

        this.contactUsForm
            .get('topic')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((topicValue: string) => {
                if (topicValue) {
                    if (topicValue.toLowerCase().includes('product')) {
                        this.isProductDropDownList = true;
                        this.isServiceDropDownList = false;
                        this.contactUsForm.get('productType').setValidators([Validators.required]);
                        this.contactUsForm.get('serviceType').clearValidators();
                        this.contactUsForm.get('serviceType').clearValidators();
                    } else if (topicValue.toLowerCase().includes('service')) {
                        this.isProductDropDownList = false;
                        this.isServiceDropDownList = true;
                        this.contactUsForm.get('serviceType').setValidators([Validators.required]);
                        this.contactUsForm.get('productType').clearValidators();
                    } else {
                        this.isProductDropDownList = false;
                        this.isServiceDropDownList = false;
                        this.contactUsForm.get('serviceType').clearValidators();
                        this.contactUsForm.get('productType').clearValidators();
                    }

                    this.contactUsForm.get('productType').reset();
                    this.contactUsForm.get('serviceType').reset();

                    this.contactUsForm.get('serviceType').updateValueAndValidity();
                    this.contactUsForm.get('productType').updateValueAndValidity();
                }
            });
    }

    handleCheckboxChange(event: MatCheckboxChange) {
        if (!event.checked) {
            this.contactUsForm.get('subscriptionCheck').setErrors({ checkBoxMustBeTrue: true });
        } else {
            this.contactUsForm.get('subscriptionCheck').setErrors(null);
        }
    }

    // * handler for handling formControl Validation
    fromControlsValidationHandler(event, index: number) {
        /**
         * * keycode   char
         * * 43         +
         * * 46         .
         * * 45         -
         * * 32         Space
         * * 63         ?
         * * 48-57      Numbers
         * * 65-90      Uppercase char
         * * 97-122     Lowercase char
         * * 8          Backspace
         * * 44         ,
         */
        let keyCode = event.charCode;
        switch (index) {
            // * restrict special char 0
            case ContactUsValidation.ResSpecialChar:
                return (keyCode > 64 && keyCode < 91) || (keyCode > 96 && keyCode < 123) || keyCode == 8 || keyCode == 32 || (keyCode >= 48 && keyCode <= 57);
            // * restrict special char and numbers 1
            case ContactUsValidation.ResSpecialCharNum:
                return (keyCode > 64 && keyCode < 91) || (keyCode > 96 && keyCode < 123) || keyCode == 8 || keyCode == 32;
            // * restrict special char except hyphen symbol 2
            case ContactUsValidation.ResSpecialCharExceptHyphen:
                return (
                    (keyCode > 64 && keyCode < 91) ||
                    (keyCode > 96 && keyCode < 123) ||
                    keyCode == 8 ||
                    keyCode == 32 ||
                    keyCode == 45 ||
                    keyCode == 46 ||
                    keyCode == 63 ||
                    (keyCode >= 48 && keyCode <= 57)
                );

            // * restrict the special characters except + and  - symbol
            case ContactUsValidation.ResSpecialCharAndCharExceptPlusHyphen:
                return keyCode == 8 || keyCode == 32 || keyCode == 45 || keyCode == 43 || (keyCode >= 48 && keyCode <= 57);

            // * restrict the special characters except + and  - symbol
            case ContactUsValidation.AddressRestriction:
                return (
                    (keyCode > 64 && keyCode < 91) ||
                    (keyCode > 96 && keyCode < 123) ||
                    keyCode == 8 ||
                    keyCode == 32 ||
                    keyCode == 45 ||
                    keyCode == 44 ||
                    (keyCode >= 48 && keyCode <= 57)
                );

            // * restrictiton for postal code
            case ContactUsValidation.PostalCodeRestriction:
                return (
                    (keyCode > 64 && keyCode < 91) ||
                    (keyCode > 96 && keyCode < 123) ||
                    keyCode == 8 ||
                    keyCode == 32 ||
                    keyCode == 45 ||
                    (keyCode >= 48 && keyCode <= 57)
                );

            // * restrict paste if string has special char 3
            case ContactUsValidation.PasteSpecialChar:
                let clipboardData1 = event.clipboardData;
                let pastedText1 = clipboardData1.getData('text');
                let format1 = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
                return format1.test(pastedText1) ? (event.preventDefault(), this.displayToast()) : null;

            // * restrict paste if string has special char except hyphen,dot,question mark symbol 4
            case ContactUsValidation.PasteSpecialCharExceptHyphen:
                let clipboardData2 = event.clipboardData;
                let pastedText2 = clipboardData2.getData('text');
                let format = /[!@#$%^&*()_+\=\[\]{};':"\\|,<>\/]+/;
                return format.test(pastedText2) ? (event.preventDefault(), this.displayToast()) : null;

            // * restrict the paste if staring has special char and numbers 5
            case ContactUsValidation.PasteSpecialCharNum:
                let clipboardData3 = event.clipboardData;
                let pastedText3 = clipboardData3.getData('text');
                let format3 = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?0123456789]+/;
                return format3.test(pastedText3) ? (event.preventDefault(), this.displayToast()) : null;

            // * restrict the paste if staring has special char and numbers 5
            case ContactUsValidation.PasteSpecialCharExceptHyphenPlusNumber:
                let clipboardData4 = event.clipboardData;
                let pastedText4 = clipboardData4.getData('text');
                let format4 = /[!@#$%^&*()_\=\[\]{};':"\\|,.<>\/?a-zA-Z]+/;
                return format4.test(pastedText4) ? (event.preventDefault(), this.displayToast()) : null;

            // * restrict the paste for Address filed
            case ContactUsValidation.PasteRestrictionAddress:
                let clipboardData5 = event.clipboardData;
                let pastedText5 = clipboardData5.getData('text');
                let format5 = /[!@#$%^&*()+_\=\[\]{};':"\\|.<>\/?]+/;
                return format5.test(pastedText5) ? (event.preventDefault(), this.displayToast()) : null;

            // * restrict paste for postal code
            case ContactUsValidation.PasteRestrictionForPostalCode:
                let clipboardData6 = event.clipboardData;
                let pastedText6 = clipboardData6.getData('text');
                let format6 = /[!@#$%^&*()_\=\[\]{};':"\\|.<>\/?,]+/;
                return format6.test(pastedText6) ? (event.preventDefault(), this.displayToast()) : null;
        }
    }

    // * helper method to import json file
    getDropDownsData() {
        this.managementApiService.getJsonFile().subscribe((data: any) => {
            let countryObject = data.ORG_DETAILS.COUNTRY;
            let productObject = data.CONTACT_US_PAGE.PRODUCTS_LIST;
            let topicObject = data.CONTACT_US_PAGE.TOPIC_LIST;
            let serviceObject = data.CONTACT_US_PAGE.SERVICE_TYPE;
            let industryObject = data.CONTACT_US_PAGE.INDUSTRY_LIST;

            this.countryList = Object.keys(countryObject).map((k) => countryObject[k]);
            this.productList = Object.keys(productObject).map((k) => productObject[k]);
            this.topicList = Object.keys(topicObject).map((k) => topicObject[k]);
            this.serviceList = Object.keys(serviceObject).map((k) => serviceObject[k]);
            this.industryList = Object.keys(industryObject).map((k) => industryObject[k]);

            for (let [key, value] of Object.entries(topicObject)) {
                if (key.toLowerCase().includes('product')) {
                    this.productRelatedTopics.push(value.toString());
                }
            }
        });
    }

    // * helper method to collect the form controls value
    getFormControlsValues() {
        let formValues = new ContactUsForm();
        formValues.message = this.contactUsForm.get('inquireyMessage').value.trim();
        formValues.topic = this.transLocoService.translate(`CONTACT_US_PAGE.TOPIC_LIST.${this.contactUsForm.get('topic').value}`); // * transalation done because this field value is language specific
        formValues.firstName = this.contactUsForm.get('firstName').value.trim();
        formValues.lastName = this.contactUsForm.get('lastName').value.trim();
        formValues.email = this.contactUsForm.get('email').value.trim();
        formValues.telephone = this.contactUsForm.get('phone').value.trim();
        formValues.company = this.contactUsForm.get('company').value.trim();
        formValues.address = this.contactUsForm.get('address').value.trim();
        formValues.city = this.contactUsForm.get('city').value.trim();
        formValues.zipCode = this.contactUsForm.get('zipCode').value.trim();
        formValues.country = this.contactUsForm.get('country').value;
        formValues.industry = this.contactUsForm.get('industry').value.trim();
        formValues.serviceUpdates = this.contactUsForm.get('subscriptionCheck').value;
        if (this.contactUsForm.get('productType')?.value) {
            formValues.productType = this.contactUsForm.get('productType').value;
        } else if (this.contactUsForm.get('serviceType')?.value) {
            formValues.serviceType = this.contactUsForm.get('serviceType').value;
        }
        this.contactUsDetails = formValues;
    }

    // * api call to sent the email
    sendEmail() {
        this.ngxUiLoaderService.start();
        this.getFormControlsValues();
        let encryptedPayload = this.encrytionDecryptionHelperService.encrypt(
            JSON.stringify(this.contactUsDetails),
            this.EnvironmentService.getConfig().azureEncKey
        ); // * encrypting api payload before sending
        this.emailSenderService.emailHandler(EmailType.ContactUsPage, undefined, encryptedPayload).subscribe(
            (data) => {
                if (data.success) {
                    this.isMailSent = true;
                    this.customToastService.successToast('Email Sent Successfully!', 'Success');
                    this.contactUsForm.reset();
                }
                this.ngxUiLoaderService.stop();
            },
            () => {
                this.customToastService.errorToast('Error while sending the email!', 'Error');
                this.ngxUiLoaderService.stop();
            }
        );
    }

    // * helper method to display toast
    displayToast() {
        this.customToastService.infoToast('Special characters are not allowed!', 'Info');
    }

    // * handle captch events
    handleCaptchaEvents(event, index) {
        this.contactUsForm.patchValue({ captchaCode: event });
        switch (index) {
            case 0: // TODO captch validation success
                break;
            case 1: // TODO captch is expired
                this.contactUsForm.get('captchaCode').reset();
            case 2:
                this.contactUsForm.get('captchaCode').reset();
                break;

            // * when captcha is ready
            case 3:
                // * user is not logged in then load the browser language based captcha
                let iframeElement = document.getElementsByTagName('iframe')[0];
                let iframeElementArray = iframeElement.src.split('&');
                let result = [];
                let language = this.transLocoService.getDefaultLang();

                iframeElementArray.forEach((item) => {
                    if (item.includes('hl')) {
                        let param;
                        if (!localStorage.getItem('msal.accessToken')) {
                            // * if user is not logged in
                            if (this.transLocoService.getAvailableLangs().some((language) => language == navigator.language.split('-')[0])) {
                                // * Displaying captcha based on the their browser language/locale
                                language = navigator.language.split('-')[0];
                            }
                            param = `hl=${language}`;
                        } else {
                            // * if user is logged in
                            param = `hl=${this.transLocoService.getActiveLang()}`;
                        }

                        result.push(param);
                    } else {
                        result.push(item);
                    }
                });
                iframeElement.src = result.join('&');
                break;
        }
    }

    ngAfterViewInit(): void {
        this.waitForCaptcha();
    }

    waitForCaptcha() {
        const interval = setInterval(() => {
            if (window.recaptchaReady && window.grecaptcha?.render) {
                clearInterval(interval);
                this.renderCaptcha();
            }
        }, 300);
    }

    renderCaptcha() {
        window.grecaptcha.render(this.captchaRef.nativeElement, {
            sitekey: this.EnvironmentService.getConfig().reCaptchaSiteKey,
            callback: (token: string) => {
                this.contactUsForm.get('captchaCode')?.setValue(token);
                this.handleCaptchaEvents(token, 0);
            },
            'expired-callback': () => {
                this.contactUsForm.get('captchaCode')?.reset();
                this.handleCaptchaEvents(null, 1);
            },
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
